import {
  ProviderUnavailableError,
  type ProviderCallOptions,
  type ProviderJobHandle,
  type ProviderVideoBgRemoveJobState,
  type VideoBgRemoveProvider,
  type VideoBgRemoveRequest,
  type VideoBgRemoveResult,
} from "@loumy/contracts";
import { safeFetchBytes } from "./safe-fetch";

// §7 `video-bg-remove`-Provider → **BEN2 v2 Video** über die fal.ai-Queue-API (D146):
// AI-Matting eines GANZEN Videos (KEIN Greenscreen nötig — Alternative zum chroma-key). Quell-
// Video rein, transparentes WebM (VP9-Alpha) raus. Gleicher FAL_API_KEY wie video/image/vision/
// image-edit/vace (EIN Vertrag). COGS ~$0.001/MP ⇒ ein 2-s-Clip ≈ $0.02 (§10.4-Pricing: 2 cr kostendeckend seit D150 — war 8).
//
// Modell-Contract `fal-ai/ben/v2/video` (fal-Doku verifiziert 2026-06-08):
//   INPUT: video_url (REQUIRED), background_color (optionale RGB-Ersatzfarbe; weglassen ⇒
//   Transparenz), output_format ∈ mp4|webm (wir: 'webm' — MP4 rendert Transparenz SCHWARZ,
//   nur WebM trägt echtes Alpha), seed. OUTPUT: { video: { url, content_type, … }, seed }.
//
// D27 (generate/poll wie die übrigen fal-Adapter): generate() submittet NUR → Handle (status_url/
// response_url in raw, DB-persistiert); poll() ist EIN schneller Status-GET. Erschöpft das
// Pro-Tick-Budget den Poll, pollt ein Resume-Tick dasselbe Handle weiter (kein Re-Submit) ⇒ die
// fal-Queue-Wartezeit ist tick-übergreifend (kein Job-Exec-Timeout-Tod). Das transparente WebM
// wird im poll()-`done`-Zweig geladen (safeFetchBytes, größen-gedeckelt) und geht base64-inline
// an die GPU-Runtime (bg-decode-alpha, libvpx-vp9). `opts.signal` bricht ab.

const FAL_QUEUE_BASE_URL = "https://queue.fal.run";
const FAL_BEN2_MODEL = "fal-ai/ben/v2/video";
// Größen-Cap des Ergebnis-WebM (DoS-/Speicher-Schutz): ein paar Sekunden Alpha-VP9 sind real
// einstellige bis niedrige zweistellige MB; großzügig wie der VACE-Cap.
const MAX_VIDEO_BYTES = 64 * 1024 * 1024;

/**
 * poll()-intern (Codex-P1-Analog zu fal-vace): ein TRANSIENTER Fehler beim Ergebnis-Download NACH
 * `COMPLETED` (fal-Result-GET oder CDN 429/5xx). Der Job IST fertig, nur der Last-Mile-Download
 * flackerte — poll() meldet dann `processing` (Retry) statt den fertigen Job terminal zu killen.
 */
class Ben2DownloadTransientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "Ben2DownloadTransientError";
  }
}

export interface FalBen2Config {
  apiKey?: string;
  baseUrl?: string;
  /** Modell-Override (Default `fal-ai/ben/v2/video`). */
  model?: string;
}

/**
 * §7 video-bg-remove → BEN2 v2 Video über fal.ai (D146, D27-Handle). `generate({videoUrl,
 * backgroundColor?, seed?})` → submit → Handle; `poll(handle)` → Status-GET, bei COMPLETED das
 * transparente WebM laden (safeFetchBytes) → Bytes. Resume-Treiber = runVideoBgRemoveProviderJob.
 */
export class FalBen2Provider implements VideoBgRemoveProvider {
  readonly id = "fal-ben2";
  readonly version = 1;
  readonly displayName = "fal.ai (BEN2 v2 Video-Matting)";

  private readonly baseUrl: string;
  private readonly model: string;

  constructor(private readonly config: FalBen2Config = {}) {
    this.baseUrl = (config.baseUrl?.trim() || FAL_QUEUE_BASE_URL).replace(/\/+$/, "");
    this.model = config.model?.trim() || FAL_BEN2_MODEL;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("video-bg-remove", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { Authorization: `Key ${apiKey}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  /** fal-Queue-URLs gegen den fal-Host validieren (SSRF/Drift, wie die übrigen fal-Adapter). */
  private requireFalUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal ben2: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).origin;
    } catch {
      throw new Error(`fal ben2: '${field}' ist keine gültige URL.`);
    }
    if (host !== new URL(this.baseUrl).origin)
      throw new Error(`fal ben2: '${field}' (${host}) liegt nicht auf dem fal-Host (${new URL(this.baseUrl).origin}).`);
    return url;
  }

  /** Ergebnis-URL gegen die fal-CDN-Hosts pinnen: das WebM geht base64-inline an die GPU-Runtime. */
  private requireFalCdnUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal ben2: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).hostname.toLowerCase().replace(/\.+$/, "");
    } catch {
      throw new Error(`fal ben2: '${field}' ist keine gültige URL.`);
    }
    const allowed = ["fal.media", "fal.run", "fal.ai"].some((d) => host === d || host.endsWith(`.${d}`));
    if (!allowed) throw new Error(`fal ben2: '${field}'-Host '${host}' liegt nicht auf einem fal-CDN-Host.`);
    return url;
  }

  /**
   * COMPLETED → transparentes WebM laden. Transiente Download-Flaps (429/5xx) → Ben2DownloadTransientError
   * (poll → 'processing'/Retry); terminale 4xx / Nicht-WebM / Host-Mismatch bleiben Hard-Fail.
   */
  private async fetchResult(
    responseUrl: string,
    apiKey: string,
    requestId: string,
    signal?: AbortSignal,
  ): Promise<VideoBgRemoveResult> {
    const resultRes = await fetch(responseUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!resultRes.ok) {
      if (resultRes.status === 429 || resultRes.status >= 500)
        throw new Ben2DownloadTransientError(`fal ben2 result: HTTP ${resultRes.status} ${resultRes.statusText} (transient)`);
      const body = (await resultRes.text().catch(() => "")).slice(0, 500);
      throw new Error(`fal ben2 result: HTTP ${resultRes.status} ${resultRes.statusText}${body ? ` — ${body}` : ""}`);
    }
    const result = (await resultRes.json()) as { video?: { url?: unknown }; seed?: unknown };
    const url = this.requireFalCdnUrl(result.video?.url, "video.url");
    let bytes: Uint8Array;
    try {
      ({ bytes } = await safeFetchBytes(url, { maxBytes: MAX_VIDEO_BYTES, signal, expectContentType: "video/" }));
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 429 || (typeof status === "number" && status >= 500))
        throw new Ben2DownloadTransientError(`fal ben2: Ergebnis-Download HTTP ${status} (transient)`);
      throw e; // content-type/Größe/Redirect/4xx/Abort → terminal
    }
    // WebM/Matroska-Magic (EBML-Header 0x1A45DFA3) — fängt HTML-Fehlerseiten/Provider-Drift ab.
    if (bytes.length < 4 || bytes[0] !== 0x1a || bytes[1] !== 0x45 || bytes[2] !== 0xdf || bytes[3] !== 0xa3) {
      throw new Error("fal ben2: Ergebnis ist kein WebM (EBML-Magic fehlt — Provider-Drift? output_format=webm gesendet?).");
    }
    return {
      videoBytes: bytes,
      provenance: {
        provider: this.id,
        modelVersion: this.model,
        requestId,
        seed: typeof result.seed === "number" && Number.isFinite(result.seed) ? result.seed : null,
      },
    };
  }

  /** D27: NUR submitten → Handle (status_url/response_url in `raw`, DB-persistiert). `opts.signal` bricht ab. */
  async generate(req: VideoBgRemoveRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const bg = req.backgroundColor;
    const bgValid =
      Array.isArray(bg) && bg.length === 3 && bg.every((c) => Number.isFinite(c) && c >= 0 && c <= 255);

    const submit = await fetch(`${this.baseUrl}/${this.model}`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify({
        video_url: req.videoUrl,
        // 'webm' = echtes Alpha (MP4 rendert Transparenz schwarz). Loumy braucht das Alpha
        // für die GPU-Decode-Stufe ⇒ hart auf webm, KEINE background_color (außer explizit angefordert).
        output_format: "webm",
        ...(bgValid ? { background_color: bg.map((c) => Math.trunc(c)) } : {}),
        ...(req.seed !== undefined && Number.isFinite(req.seed) ? { seed: Math.trunc(req.seed) } : {}),
      }),
      signal,
    });
    if (!submit.ok) {
      const body = submit.status < 500 ? (await submit.text().catch(() => "")).slice(0, 500) : "";
      throw new Error(`fal ben2 submit: HTTP ${submit.status} ${submit.statusText}${body ? ` — ${body}` : ""}`);
    }
    const sub = (await submit.json()) as { request_id?: string; status_url?: unknown; response_url?: unknown };
    if (!sub.request_id) throw new Error("fal ben2 submit: keine request_id in der Antwort.");
    const statusUrl = this.requireFalUrl(sub.status_url, "status_url");
    const responseUrl = this.requireFalUrl(sub.response_url, "response_url");
    return {
      providerId: this.id,
      providerVersion: this.version,
      externalId: sub.request_id,
      raw: { statusUrl, responseUrl },
    };
  }

  /** D27: EIN Status-GET. IN_QUEUE→pending, IN_PROGRESS→processing, COMPLETED→WebM laden→done. */
  async poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderVideoBgRemoveJobState> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const raw = (handle.raw ?? {}) as { statusUrl?: unknown; responseUrl?: unknown };
    const statusUrl = this.requireFalUrl(raw.statusUrl, "status_url (handle)");
    const responseUrl = this.requireFalUrl(raw.responseUrl, "response_url (handle)");

    const statusRes = await fetch(statusUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!statusRes.ok) {
      if (statusRes.status === 429 || statusRes.status >= 500) return { status: "processing" };
      const body = (await statusRes.text().catch(() => "")).slice(0, 500);
      return { status: "failed", error: `fal ben2 status: HTTP ${statusRes.status} ${statusRes.statusText}${body ? ` — ${body}` : ""}` };
    }
    const status = (await statusRes.json()) as { status?: string; error?: string };
    if (status.error) return { status: "failed", error: `fal ben2: ${status.error}` };
    switch (status.status) {
      case "COMPLETED":
        try {
          return { status: "done", output: await this.fetchResult(responseUrl, apiKey, handle.externalId, signal) };
        } catch (e) {
          if (e instanceof Ben2DownloadTransientError) return { status: "processing" };
          throw e;
        }
      case "IN_QUEUE":
        return { status: "pending" };
      case "IN_PROGRESS":
        return { status: "processing" };
      default:
        return { status: "failed", error: `fal ben2: unerwarteter Status '${status.status ?? "—"}' (terminal ohne error-Feld?).` };
    }
  }
}
